import React, { useEffect, useState } from 'react';
import { CaretDown, Handbag, Heart, Leaf, ThumbsUp, ThumbsDown } from 'phosphor-react';
import { Park } from '@phosphor-icons/react';
import AffirmationCard from './components/AffirmationCard';
import BottomNavigation from '../../navigation/BottomNavigation';
import { Card } from '../../components/shared/Card';
import { ReactionButtons } from '../../components/shared/ReactionButtons';

/**
 * Daily Affirmation Screen
 * Displays personalized daily affirmation based on user intentions
 * User must tap to reveal today's affirmation
 */
export const AffirmationScreen: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeAlignmentTab, setActiveAlignmentTab] = useState<'number' | 'stone' | 'affirmation'>('number');
  const [isDailyAlignmentsExpanded, setIsDailyAlignmentsExpanded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate opacity based on scroll position (fade out over 100px of scroll)
  const gradientOpacity = Math.max(0, 1 - scrollY / 100);

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Gradient header that fades on scroll */}
      <div 
        className="fixed top-0 left-0 right-0 h-[169px] z-0 pointer-events-none transition-opacity duration-300"
        style={{
          backgroundImage: 'url(/images/header/headerimg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: gradientOpacity,
        }}
      >
        {/* Gradient overlay */}
        <div 
          className="w-full h-full"
          style={{
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 1) 83.8%)',
          }}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 pb-0 overflow-hidden">
        <div className="relative z-20 pt-[50px] pb-[106px] h-full flex flex-col min-h-0 overflow-y-auto">
          {/* Top bar - Welcome message, profile, calendar */}
          <div className="px-screen-padding mb-6 flex-shrink-0">
            <div className="relative" style={{ height: '109px' }}>
              {/* Profile picture - positioned at right */}
              <div className="absolute right-0 top-[6px] w-[40px] h-[40px] rounded-full bg-grey-bg3 overflow-hidden">
                <img 
                  src="/images/profile/profilepicture.png" 
                  alt="Profile picture" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Welcome message and Calendar */}
              <div className="absolute top-[56px] left-0 right-0 flex items-center justify-between">
                {/* Welcome message */}
                <div className="flex items-center">
                  <span
                    style={{
                      fontFamily: 'Didot, serif',
                      fontStyle: 'italic',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '24px',
                      letterSpacing: '-1px',
                      color: '#CCCCCC',
                    }}
                  >
                    Hello,
                  </span>
                  <span
                    style={{
                      fontFamily: 'Didot, serif',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '24px',
                      letterSpacing: '-1px',
                      color: '#FFFFFF',
                      marginLeft: '8px',
                    }}
                  >
                    Sean
                  </span>
                </div>
                
                {/* Calendar button */}
                <div className="flex items-center px-3 py-1.5 rounded-lg border border-white-p40/10 bg-white-p40/5 backdrop-blur-sm transition-all hover:border-white-p40/20" style={{ gap: '4px' }}>
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '16.8px',
                      color: '#FFFFFF',
                    }}
                  >
                    Calendar
                  </span>
                  <CaretDown size={14} weight="bold" className="text-white-pure" />
                </div>
              </div>
              
              {/* Subtitle */}
              <div className="absolute top-[83px] left-0">
                <span
                  style={{
                    fontFamily: 'Space Grotesk',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '19.6px',
                    color: '#CCCCCC',
                  }}
                >
                  The Day of Water and Depth
                </span>
              </div>
            </div>
          </div>
          
          {/* All content in one continuous column */}
          <div className="px-screen-padding flex flex-col flex-1 min-h-0 gap-4">
            {/* Affirmation card - fills available space */}
            <div className="flex-1 flex flex-col min-h-0">
              <AffirmationCard fillHeight={true} />
            </div>
            
            {/* Growth & Reflections card */}
            <Card 
              variant="default" 
              className="flex-shrink-0 !px-3 !py-4 relative"
            >
              <div className="flex flex-col gap-4">
                {/* Title and 3-dots menu */}
                <div className="flex items-center justify-between">
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '16.8px',
                      letterSpacing: '0.28px',
                      color: '#E6E6E6',
                    }}
                  >
                    Growth & Reflections
                  </span>
                  {/* 3 dots menu */}
                  <button
                    className="flex items-center justify-center gap-1 transition-opacity active:opacity-80"
                    style={{
                      width: '20px',
                      height: '8px',
                    }}
                    aria-label="More options"
                  >
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                  </button>
                </div>
                
                {/* Reflection card with image background */}
                <div
                  className="rounded-[10px] overflow-hidden relative"
                  style={{
                    height: '162px',
                    backgroundImage: 'url(/images/affirmation/growth&reflection.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  {/* Soft black overlay */}
                  <div 
                    className="absolute inset-0"
                    style={{
                      background: 'rgba(0, 0, 0, 0.4)',
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center z-10 px-6">
                    <span
                      style={{
                        fontFamily: 'Didot, serif',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '20px',
                        lineHeight: '28px',
                        letterSpacing: '0.4px',
                        color: '#FFFFFF',
                        textAlign: 'center',
                      }}
                    >
                      In Stillness, Truth Finds Its Voice
                    </span>
                  </div>
                </div>
                
                {/* Recommendation section */}
                <div className="flex flex-col gap-2">
                  <div>
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '14.4px',
                        letterSpacing: '0.24px',
                        color: '#E6E6E6',
                      }}
                    >
                      Recommendation
                    </span>
                  </div>
                  <div>
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '19.6px',
                        letterSpacing: '0.28px',
                        color: '#CCCCCC',
                      }}
                    >
                      Today's turning point lies in connections, collaboration, and the exchange of wisdom.
                    </span>
                  </div>
                </div>
                
                {/* Reaction buttons */}
                <ReactionButtons />
              </div>
            </Card>
            
            {/* Category Cards - Wellness, Career, Love, Growth */}
            {[
              {
                title: 'Wellness',
                icon: <Park size={20} weight="regular" style={{ color: '#FE5706' }} />,
                description: 'Nurture your body and mind. Balance comes from consistent care and mindful choices.'
              },
              {
                title: 'Career',
                icon: <Handbag size={20} weight="regular" style={{ color: '#FE5706' }} />,
                description: 'Build your path with intention. Strategic steps today create opportunities tomorrow.'
              },
              {
                title: 'Love',
                icon: <Heart size={20} weight="regular" style={{ color: '#FE5706' }} />,
                description: 'Open your heart with compassion. Deep connections grow from understanding and presence.'
              },
              {
                title: 'Growth',
                icon: <Leaf size={20} weight="regular" style={{ color: '#FE5706' }} />,
                description: 'Embrace learning and self-discovery. Every experience is a step toward your highest self.'
              }
            ].map((category) => (
              <Card 
                key={category.title}
                variant="default" 
                className="flex-shrink-0 !px-4 !py-4"
              >
                <div className="flex flex-col gap-3">
                  {/* Title with icon */}
                  <div className="flex items-center gap-3">
                    {category.icon}
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '16.8px',
                        letterSpacing: '0.28px',
                        color: '#E6E6E6',
                      }}
                    >
                      {category.title}
                    </span>
                  </div>
                  
                  {/* Description */}
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '19.6px',
                      letterSpacing: '0.28px',
                      color: '#CCCCCC',
                    }}
                  >
                    {category.description}
                  </span>
                </div>
              </Card>
            ))}
            
            {/* Your Daily Alignments card */}
            <Card variant="default" className="flex-shrink-0 !px-3 !py-4">
              <div className="flex flex-col gap-4">
                {/* Title and 3-dots menu */}
                <div className="flex items-center justify-between">
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '16.8px',
                      letterSpacing: '0.28px',
                      color: '#E6E6E6',
                    }}
                  >
                    Your Daily Alignments
                  </span>
                  {/* 3 dots menu */}
                  <button
                    className="flex items-center justify-center gap-1 transition-opacity active:opacity-80"
                    style={{
                      width: '20px',
                      height: '8px',
                    }}
                    aria-label="More options"
                  >
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                  </button>
                </div>
                
                {/* Tabs - Always shown */}
                <div className="flex gap-4">
                  {/* Number Tab */}
                  <div
                    className={`flex-1 rounded-[15px] overflow-hidden relative cursor-pointer ${activeAlignmentTab === 'number' ? 'border border-white-p60' : ''}`}
                    style={{
                      height: '160px',
                      backgroundImage: 'url(/images/affirmation/Daily_alignments/7.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      opacity: activeAlignmentTab === 'number' ? 1 : 0.5,
                    }}
                    onClick={() => setActiveAlignmentTab('number')}
                  >
                    {/* Overlay for text readability */}
                    <div 
                      className="absolute inset-0"
                      style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                      }}
                    />
                    <div className="p-3 flex flex-col gap-2 relative z-10 h-full justify-end">
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: activeAlignmentTab === 'number' ? '#E6E6E6' : '#666666',
                        }}
                      >
                        Number
                      </span>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: activeAlignmentTab === 'number' ? '#E6E6E6' : '#666666',
                        }}
                      >
                        Seven (7)
                      </span>
                    </div>
                  </div>
                  
                  {/* Stone Tab */}
                  <div
                    className={`flex-1 rounded-[15px] overflow-hidden relative cursor-pointer ${activeAlignmentTab === 'stone' ? 'border border-white-p60' : ''}`}
                    style={{
                      height: '160px',
                      backgroundImage: 'url(/images/affirmation/Daily_alignments/Citrine.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      opacity: activeAlignmentTab === 'stone' ? 1 : 0.5,
                    }}
                    onClick={() => setActiveAlignmentTab('stone')}
                  >
                    {/* Overlay for text readability */}
                    <div 
                      className="absolute inset-0"
                      style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                      }}
                    />
                    <div className="p-3 flex flex-col gap-2 relative z-10 h-full justify-end">
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: activeAlignmentTab === 'stone' ? '#E6E6E6' : '#666666',
                        }}
                      >
                        Stone
                      </span>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: activeAlignmentTab === 'stone' ? '#E6E6E6' : '#666666',
                        }}
                      >
                        Citrine
                      </span>
                    </div>
                  </div>
                  
                  {/* Affirmation Tab */}
                  <div
                    className={`flex-1 rounded-[15px] overflow-hidden relative cursor-pointer ${activeAlignmentTab === 'affirmation' ? 'border border-white-p60' : ''}`}
                    style={{
                      height: '160px',
                      backgroundImage: 'url(/images/affirmation/Daily_alignments/blackhole.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      opacity: activeAlignmentTab === 'affirmation' ? 1 : 0.5,
                    }}
                    onClick={() => setActiveAlignmentTab('affirmation')}
                  >
                    {/* Overlay for text readability */}
                    <div 
                      className="absolute inset-0"
                      style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                      }}
                    />
                    <div className="p-3 flex flex-col gap-2 relative z-10 h-full justify-end">
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: activeAlignmentTab === 'affirmation' ? '#E6E6E6' : '#666666',
                        }}
                      >
                        Affirmation
                      </span>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: activeAlignmentTab === 'affirmation' ? '#E6E6E6' : '#666666',
                        }}
                      >
                        The Still
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Detail Section - Always shown */}
                <div className="flex flex-col gap-2">
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: '#E6E6E6',
                        }}
                      >
                        {activeAlignmentTab === 'number' ? 'Number' : activeAlignmentTab === 'stone' ? 'Stone' : 'Affirmation'}
                      </span>
                    </div>
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '19.6px',
                          letterSpacing: '0.28px',
                          color: '#CCCCCC',
                        }}
                      >
                        {activeAlignmentTab === 'number' 
                          ? "Today carries the energy of Number 7, introspection, wisdom, mystery, and spiritual elevation."
                          : activeAlignmentTab === 'stone'
                          ? "Citrine brings clarity and abundance to your path. Let its golden light illuminate forward."
                          : "The Still speaks of inner peace and quiet moments. Find the answers you seek within."
                        }
                      </span>
                    </div>
                  </div>
                
                {/* Reaction buttons with expand/collapse */}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <button
                      className="flex items-center justify-center rounded-full transition-opacity active:opacity-80"
                      style={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#272727',
                      }}
                      aria-label="Ask GodGPT"
                    >
                      <img 
                        src="/svg/godgpticon.svg" 
                        alt="GodGPT Icon" 
                        className="w-4 h-4"
                      />
                    </button>
                    <button
                      className="flex items-center justify-center rounded-full transition-opacity active:opacity-80"
                      style={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#272727',
                      }}
                      aria-label="Thumbs up"
                    >
                      <ThumbsUp size={16} weight="regular" className="text-white-pure" />
                    </button>
                    <button
                      className="flex items-center justify-center rounded-full transition-opacity active:opacity-80"
                      style={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#272727',
                      }}
                      aria-label="Thumbs down"
                    >
                      <ThumbsDown size={16} weight="regular" className="text-white-pure" />
                    </button>
                  </div>
                  {/* Expand/Collapse button */}
                  <button
                    onClick={() => setIsDailyAlignmentsExpanded(!isDailyAlignmentsExpanded)}
                    className="rounded-full flex items-center justify-center transition-opacity active:opacity-80"
                    style={{
                      width: '40px',
                      height: '30px',
                      background: 'rgb(39, 39, 39)',
                      borderRadius: '99px',
                    }}
                    aria-label={isDailyAlignmentsExpanded ? 'Collapse' : 'Expand'}
                  >
                    <CaretDown 
                      size={16} 
                      weight="regular" 
                      className="text-white-pure"
                      style={{
                        transform: isDailyAlignmentsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  </button>
                </div>
              </div>
            </Card>
            
            {/* Ask and reflect card - as button */}
            <button
              className="flex-shrink-0 cursor-pointer h-full flex flex-col items-center justify-center rounded-2xl p-6 bg-grey-bg1 relative overflow-hidden min-h-0 border border-white-p40/20 transition-opacity active:opacity-80"
              style={{
                minHeight: '120px',
                height: '120px',
              }}
              onClick={() => {
                console.log('Ask and reflect button clicked');
              }}
            >
              {/* Background image */}
              <div
                className="absolute inset-0 rounded-2xl"
                style={{
                  backgroundImage: 'url(/images/askgodgpt/askgodgpt.png)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  zIndex: 1,
                }}
              />
              {/* Black overlay */}
              <div 
                className="absolute inset-0 rounded-2xl pointer-events-none"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  zIndex: 2,
                }}
              />
              <div className="relative z-10 flex items-center justify-center gap-2">
                <span
                  className="transition-colors"
                  style={{
                    fontFamily: 'Space Grotesk',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '28px',
                    color: '#E6E6E6',
                  }}
                >
                  Ask and reflect with
                </span>
                <div className="flex items-center gap-1.5">
                  <img 
                    src="/svg/godgpticon.svg" 
                    alt="GodGPT Icon" 
                    className="w-[13px] h-4 transition-all group-active:brightness-0"
                  />
                  <span
                    className="transition-colors"
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '28px',
                      color: '#E6E6E6',
                    }}
                  >
                    GodGPT
                  </span>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default AffirmationScreen;
